import React, { useCallback, useEffect, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfo } from '@fortawesome/free-solid-svg-icons';
import { CHART_SERIES_PALETTE } from 'constants/colors';
import { RodadaInfo, Rodadas } from 'helper/flux/automatic';
import StatusBadge from 'components/DesignSystem/StatusBadge';
import './index.scss';

export interface RoundInfoPopoverProps {
  /**
   * Ordem COMPLETA de Object.keys(data) do gráfico Daily (inclui ACOMPH/MLT):
   * o índice do cenário NESTA lista define a cor da série na paleta — a
   * bolinha da tabela precisa bater com a cor real da linha do gráfico,
   * mesmo que pseudo-cenários não tenham linha na tabela.
   */
  scenarioNames: string[],
  rodadas?: Rodadas,
}

interface Row {
  name: string,
  index: number,
  info: RodadaInfo,
}

/** "dd/mm/yyyy" → "dd/mm" (contrato garante o formato; corte posicional). */
const shortDate = (date: string) => date.slice(0, 5);

export default function RoundInfoPopover({
  scenarioNames,
  rodadas = undefined,
}: RoundInfoPopoverProps) {
  const [show, setShow] = useState(false);
  const close = useCallback(() => setShow(false), []);

  // Fecha com Esc enquanto aberto (o rootClose do OverlayTrigger cobre o
  // clique fora; o listener explícito garante o Esc de forma determinística).
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [show, close]);

  // Apenas cenários com metadados de rodada, na ordem de scenarioNames.
  const rows: Row[] = scenarioNames
    .map((name, index) => ({ name, index, info: rodadas?.[name] }))
    .filter((row): row is Row => row.info !== undefined);

  // BE antigo no ar (sem rodadas) ou nenhum cenário real: fallback silencioso.
  if (rows.length === 0) {
    return null;
  }

  const popover = (
    <Popover id="flux-daily-round-info-popover" className="round-info-popover">
      <Popover.Header as="h3" className="round-info-popover__header">
        <span>Informações da rodada</span>
        <button
          type="button"
          className="round-info-popover__close"
          aria-label="Fechar"
          onClick={close}
        >
          ×
        </button>
      </Popover.Header>
      <Popover.Body className="round-info-popover__body">
        {/* Muitos cenários: só a tabela rola; header sticky, rodapé fixo. */}
        <div className="round-info-popover__table-wrap">
          <table className="round-info-popover__table">
            <thead>
              <tr>
                <th scope="col">Cenário</th>
                <th scope="col">ACOMPH</th>
                <th scope="col">Rodada</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ name, index, info }) => (
                <tr key={name}>
                  <td>
                    <div className="round-info-popover__scenario">
                      <span
                        className="round-info-popover__dot"
                        style={{
                          backgroundColor:
                            CHART_SERIES_PALETTE[index % CHART_SERIES_PALETTE.length],
                        }}
                        data-testid={`round-info-dot-${name}`}
                        aria-hidden="true"
                      />
                      <span className="round-info-popover__name" title={name}>{name}</span>
                    </div>
                  </td>
                  <td className="round-info-popover__date">{shortDate(info.acomph)}</td>
                  <td className="round-info-popover__date">{shortDate(info.rodada)}</td>
                  <td><StatusBadge status={info.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Regra binária: só existem Pré-PSAT (subdir sem sufixo -PSAT) e
            Pós-PSAT (subdir com o sufixo) — não há estado intermediário. */}
        <div className="round-info-popover__footer" aria-label="Progressão de status">
          <StatusBadge status="pre-psat" />
          <span className="round-info-popover__chevron" aria-hidden="true">›</span>
          <StatusBadge status="pos-psat" />
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      show={show}
      onToggle={setShow}
      trigger="click"
      placement="bottom"
      rootClose
      overlay={popover}
    >
      <button
        type="button"
        className={`round-info-popover__trigger${show ? ' round-info-popover__trigger--open' : ''}`}
        aria-label="Informações da rodada"
        aria-expanded={show}
      >
        <FontAwesomeIcon icon={faInfo} />
      </button>
    </OverlayTrigger>
  );
}
